import { action } from '@ember/object';
import { next, schedule } from '@ember/runloop';

import $ from 'jquery';

import type { ColorMappings } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import type { Option as ColorOption } from 'webapp/components/common/popups/popup-colors';
import type { Select2Instance } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';
import type { ArgsMultiple as BaseArgs } from 'webapp/components/form/select-attributes';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';

export type { ColorMappings };

interface Args extends BaseArgs {
	colorMappings: ColorMappings;
	colorOptions: ColorOption[];
	changeColor: (attribute: string, color: number | string | null) => void;
}

export default class FormSelectAttributesWithColorsComponent extends FormSelectAttributesComponent<Args> {
	@action
	override onChange() {
		super.onChange();
		this.updateCustomElementsForSelectionAfterRender();
	}

	@action
	override onCloseMultiple(_fn: unknown, selected: string[] | undefined) {
		super.onCloseMultiple(_fn, selected);
		this.updateCustomElementsForSelection();
	}

	@action
	override setupView(node: HTMLElement) {
		this.selectNode = node as HTMLSelectElement;
		this.updateCustomElementsForSelectionAfterRender();
	}

	override onOptionsUpdate() {
		next(() => {
			this.updateCustomElementsForSelectionAfterRender();
		});
	}

	updateCustomElementsForSelectionAfterRender() {
		schedule('afterRender', this.updateCustomElementsForSelection);
	}

	@action
	updateCustomElementsForSelection() {
		const select2Instance = this.getSelect2Instance();

		if (!select2Instance) {
			return;
		}

		select2Instance.$selection.addClass('select2-selection--with-icon');

		// adding elements, if necessary
		this.getSelect2ChoiceElements().each((_idx: number, node: HTMLElement) => {
			const el = $(node);

			let colorPickerEl = el.find('.bs__attribute__color-picker');
			if (!colorPickerEl.length) {
				// attr, not data(): data() would turn numeric-looking values (e.g. 2024) into numbers
				const id = el.find('.tooltip').attr('data-id')!; // set by FormSelect.formatResult
				colorPickerEl = $(
					`<a class="bs__attribute__color-picker"><ui-icon name="RaindropAuto" size="sm"></ui-icon></a>`,
				);
				colorPickerEl.on('click', (e) => {
					this.openColorOptions(e.target, id);
					return false;
				});
				colorPickerEl.insertAfter(el.find('.select2-selection__choice__remove'));
			}

			const id = el.find('.tooltip').attr('data-id')!,
				hexColor = this.mmMetricsService.getHexColorForAttribute(
					id,
					this.args.colorMappings,
					this.args.colorOptions,
				);

			const iconEl = colorPickerEl.find('ui-icon');
			iconEl.attr('name', hexColor ? 'RaindropFill' : 'RaindropAuto');
			if (hexColor) {
				iconEl.attr('fill', hexColor);
			} else {
				iconEl.removeAttr('fill');
			}
			colorPickerEl.toggleClass('has-color', !!hexColor);
		});
	}

	@action
	openColorOptions(attachTo: HTMLElement, attribute: string) {
		// close dropdown, in case it was open
		const selectNode = this.selectNode;
		if (selectNode) {
			$(selectNode).select2('close');
		}

		this.mmMetricsService.openColorPickerForAttribute(
			attachTo,
			attribute,
			this.args.colorMappings,
			this.args.colorOptions,
			(color: string | number | null) => {
				this.args.changeColor(attribute, color);
			},
		);
	}

	private getSelect2Instance(): Select2Instance | undefined {
		const selectNode = this.selectNode;
		if (!selectNode) {
			return;
		}
		return $(selectNode).data('select2') as Select2Instance | undefined;
	}

	private getSelect2ChoiceElements(): JQuery<HTMLElement> {
		const select2Instance = this.getSelect2Instance();
		if (!select2Instance) {
			return $();
		}

		return select2Instance.$selection.find('.select2-selection__choice');
	}
	<template>
		{{! all the same as Form::SelectAttributes except the last did-update for colors }}

		<FormSelectComponent
			name={{this.uniqueId}}
			@selected={{this.selectedMultiple}}
			@options={{this.options}}
			@onChange={{this.onChange}}
			@isNumeric={{false}}
			@isMultiple={{true}}
			@placeholder={{this.placeholder}}
			@maxSelectionLength={{@maxSelectionLength}}
			@isLoading={{this.isLoading}}
			@isDisabled={{@isDisabled}}
			@isRequired={{@isRequired}}
			@tooltipOnSelectionFromProperty=""
			@onCustomAdd={{this.onCustomAdd}}
			@onOpen={{this.onOpen}}
			@onClose={{this.onCloseMultiple}}
			@beforeChange={{this.beforeChangeMultiple}}
			@showFloatlabel={{@showFloatlabel}}
			@theme={{@theme}}
			@size={{@size}}
			{{didInsert this.setupView}}
			{{didUpdate this.onMetricChange @metricSetupForQuery.metric}}
			{{didUpdate this.onSelectedChange @selected}}
			{{didUpdate this.onPropChange @isMultiple @disabledItemsList}}
			{{didUpdate this.updateCustomElementsForSelection @colorMappings}}
		/>
	</template>
}
